/**
 * Keyboard Handling for Capacitor
 * Manages keyboard events and adjustments for mobile
 */

import { Keyboard, KeyboardInfo, KeyboardResize } from '@capacitor/keyboard';
import { isNativePlatform, isIOS, isAndroid } from './platform';

export interface KeyboardCallbacks {
  onShow?: (height: number) => void;
  onHide?: () => void;
  onWillShow?: (height: number) => void;
  onWillHide?: () => void;
}

let isInitialized = false;
let keyboardCallbacks: KeyboardCallbacks = {};

/**
 * Initialize keyboard handling
 */
export async function initializeKeyboard(callbacks: KeyboardCallbacks = {}): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  if (isInitialized) {
    return;
  }

  keyboardCallbacks = callbacks;

  // Set keyboard resize behavior
  try {
    await Keyboard.setResizeMode({ mode: KeyboardResize.Body });
  } catch (error) {
    console.warn('Could not set keyboard resize mode:', error);
  }

  // Add keyboard listeners
  await addKeyboardListeners();

  isInitialized = true;
}

/**
 * Add keyboard event listeners
 */
async function addKeyboardListeners(): Promise<void> {
  // Keyboard will show (iOS only)
  if (isIOS()) {
    await Keyboard.addListener('keyboardWillShow', (info: KeyboardInfo) => {
      keyboardCallbacks.onWillShow?.(info.keyboardHeight);
    });

    await Keyboard.addListener('keyboardWillHide', () => {
      keyboardCallbacks.onWillHide?.();
    });
  }

  // Keyboard did show
  await Keyboard.addListener('keyboardDidShow', (info: KeyboardInfo) => {
    keyboardCallbacks.onShow?.(info.keyboardHeight);
  });

  // Keyboard did hide
  await Keyboard.addListener('keyboardDidHide', () => {
    keyboardCallbacks.onHide?.();
  });
}

/**
 * Show the keyboard
 */
export async function showKeyboard(): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  try {
    await Keyboard.show();
  } catch (error) {
    console.warn('Could not show keyboard:', error);
  }
}

/**
 * Hide the keyboard
 */
export async function hideKeyboard(): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  try {
    await Keyboard.hide();
  } catch (error) {
    console.warn('Could not hide keyboard:', error);
  }
}

/**
 * Set keyboard accessory bar visibility (iOS only)
 * The accessory bar is the bar above the keyboard with prev/next/done buttons
 */
export async function setAccessoryBarVisible(visible: boolean): Promise<void> {
  if (!isIOS()) {
    return;
  }

  try {
    await Keyboard.setAccessoryBarVisible({ isVisible: visible });
  } catch (error) {
    console.warn('Could not set accessory bar visibility:', error);
  }
}

/**
 * Enable/disable keyboard scroll (iOS only)
 */
export async function setScrollEnabled(enabled: boolean): Promise<void> {
  if (!isIOS()) {
    return;
  }

  try {
    await Keyboard.setScroll({ isDisabled: !enabled });
  } catch (error) {
    console.warn('Could not set keyboard scroll:', error);
  }
}

/**
 * Set keyboard resize mode
 */
export async function setResizeMode(mode: KeyboardResize): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  try {
    await Keyboard.setResizeMode({ mode });
  } catch (error) {
    console.warn('Could not set resize mode:', error);
  }
}

/**
 * Remove keyboard listeners
 */
export async function removeKeyboardListeners(): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  await Keyboard.removeAllListeners();
  isInitialized = false;
  keyboardCallbacks = {};
}

/**
 * Utility hook for keyboard-aware scrolling
 * Returns the current keyboard height
 */
export function createKeyboardAwareContainer(
  onKeyboardChange: (height: number, isVisible: boolean) => void,
): () => Promise<void> {
  let cleanup: (() => void) | null = null;

  const setup = async () => {
    if (!isNativePlatform()) {
      return;
    }

    const showListener = await Keyboard.addListener('keyboardDidShow', (info) => {
      onKeyboardChange(info.keyboardHeight, true);
    });

    const hideListener = await Keyboard.addListener('keyboardDidHide', () => {
      onKeyboardChange(0, false);
    });

    cleanup = () => {
      showListener.remove();
      hideListener.remove();
    };
  };

  setup();

  return async () => {
    cleanup?.();
  };
}
